import { Locale } from '@lalalilo/enums'
import React from 'react'
import { OutboundLink } from 'react-ga'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { fullHeightCss } from '../../common/style'
import { Color } from '../../common/style/color'
import { HomePageIllustration } from '../../common/style/components/home-page-illustration'
import { Image as StyledImage } from '../../common/style/components/image'
import { ShadowLink } from '../../common/style/components/links'
import { mobile, tablet } from '../../common/style/media-queries'
import { TeachAppPath, useTeachAppLink } from '../../external-links'
import { headerHeightPx } from '../../header/index'
import { getLocale } from '../../i18n/locale'
import { Arrow } from './Arrow'
import { CseLogo } from './CseLogo'
import { separator } from './resources'

export const CallToActionSection = () => {
  const [t] = useTranslation('cta')

  return (
    <Container>
      <DesktopContainer>
        <HeaderContainer>
          <ContentContainer>
            <Title>{t('title')}</Title>
            <Description>{t('description')}</Description>
            <DesktopCTALink>{t('button')}</DesktopCTALink>
          </ContentContainer>
          <HomePageIllustration />
        </HeaderContainer>
        <DesktopArrow offset={-(headerHeightPx.desktop + 20)} />
        <Separator src={separator} />
        {getLocale() === Locale.EN && <CseLogo />}
      </DesktopContainer>
      <MobileCTALink>{t('button')}</MobileCTALink>
      <MobileArrow offset={-(headerHeightPx.mobile + 20)} />
    </Container>
  )
}

const Container = styled.section`
  position: relative;
  width: 100%;
  ${fullHeightCss}

  background-color: white;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  @media ${mobile} {
    align-items: center;
  }
`

const backgroundPadding = 10
const DesktopContainer = styled.div`
  position: relative;
  flex-grow: 1;
  margin: 0 20px 20px 20px;
  padding: 0 ${backgroundPadding}px;
  width: calc(100% - ${40 + backgroundPadding * 2}px);
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background-color: ${Color.blue_600_50};

  @media ${mobile} {
    width: calc(100% - ${20 + backgroundPadding * 2}px);
    margin: 0 10px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;

  @media ${tablet} {
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
  }
`

const Separator = styled(StyledImage)`
  position: absolute;
  display: block;
  bottom: -1px;
  width: 100%;
`

const Title = styled.h1`
  margin: 0;
  max-width: 500px;
  z-index: 0;
  text-align: center;
  font-size: 30px;
  color: ${Color.grey_600};
  font-weight: bold;

  @media ${tablet} {
    margin-top: 20px;
  }

  @media ${mobile} {
    font-size: 20px;
    line-height: 24px;
  }
`

const Description = styled.p`
  margin: 30px;
  max-width: 500px;
  width: 100%;
  z-index: 2;
  font-size: 18px;
  font-family: Circular Std;
  color: ${Color.grey_600};
  line-height: 28px;
  text-align: center;

  @media ${mobile} {
    font-size: 16px;
    line-height: 24px;
  }
`

const OutboundShadowLink = ShadowLink as any
const CallToActionLink: React.FC = ({ children, ...props }) => {
  const teachAppLink = useTeachAppLink()

  return (
    <OutboundShadowLink
      as={OutboundLink}
      to={teachAppLink(TeachAppPath.GLOBAL_SIGN_UP)}
      eventLabel="signup get started top"
      {...props}
    >
      {children}
    </OutboundShadowLink>
  )
}

const DesktopCTALink = styled(CallToActionLink)`
  @media ${mobile} {
    display: none;
  }
`

const MobileCTALink: React.FC = ({ children, ...props }) => (
  <MobileLinkContainer>
    <CallToActionLink {...props}>{children}</CallToActionLink>
  </MobileLinkContainer>
)

const MobileLinkContainer = styled.div`
  display: none;

  @media ${mobile} {
    margin: 40px 10px 60px 10px;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const DesktopArrow = styled(Arrow).attrs({ ariaLabel: 'Go to next section' })`
  position: absolute;
  z-index: 10;
  bottom: -8px;

  @media ${mobile} {
    display: none;
  }
`

const MobileArrow = styled(Arrow).attrs({ ariaLabel: 'Go to next section' })`
  display: none;
  @media ${mobile} {
    display: block;
    margin-bottom: 20px;
  }
`
